"use client";

import { useEffect, useState } from "react";
import {
  FolderKanban,
  LayoutDashboard,
  LayoutGrid,
  MoreHorizontal,
  Sparkles,
} from "lucide-react";
import { useParams, usePathname, useRouter } from "next/navigation";

import GradientButton from "@/components/gradient-button";
import { MobileMoreSheet } from "@/components/mobile/mobile-more-sheet";
import { useChatSwitch } from "@/hooks/use-chat-switch";
import { isChatLayoutOpen } from "@/lib/chat/chat-layout-persistence";
import { cn, isValidOrgId } from "@/lib/utils";

const LAST_PRODUCTS_PATH_KEY = "kriya-mobile-last-products-path";
const LAST_PROJECTS_PATH_KEY = "kriya-mobile-last-projects-path";

type MobileTabId = "dashboard" | "products" | "projects" | "more";

function readSessionPath(key: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionPath(key: string, path: string): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(key, path);
  } catch {
    // ignore quota / private mode
  }
}

function pathWithoutQuery(path: string): string {
  return path.split("?")[0] ?? path;
}

export function MobileTabBar(): JSX.Element | null {
  const pathname = usePathname();
  const router = useRouter();
  const params = useParams();
  const orgId = typeof params?.orgId === "string" ? params.orgId : "";
  const { selectedView, handleViewChange } = useChatSwitch();
  const [moreOpen, setMoreOpen] = useState(false);

  const validOrgId = isValidOrgId(orgId) ? orgId : null;

  useEffect(() => {
    if (!pathname || !validOrgId) {
      return;
    }
    const path = pathWithoutQuery(pathname);
    if (path.includes(`/${validOrgId}/products`)) {
      writeSessionPath(LAST_PRODUCTS_PATH_KEY, path);
    }
    if (path.includes(`/${validOrgId}/projects`)) {
      writeSessionPath(LAST_PROJECTS_PATH_KEY, path);
    }
  }, [pathname, validOrgId]);

  if (!validOrgId) {
    return null;
  }

  const chatOpen = isChatLayoutOpen(selectedView);
  const path = pathWithoutQuery(pathname ?? "");

  let activeTab: MobileTabId | null = null;
  if (moreOpen) {
    activeTab = "more";
  } else if (!chatOpen && path.includes(`/${validOrgId}/products`)) {
    activeTab = "products";
  } else if (!chatOpen && path.includes(`/${validOrgId}/projects`)) {
    activeTab = "projects";
  } else if (!chatOpen && path.includes(`/${validOrgId}/dashboard`)) {
    activeTab = "dashboard";
  }

  const defaultProductsHref = `/${validOrgId}/products`;
  const defaultProjectsHref = `/${validOrgId}/projects`;
  const dashboardHref = `/${validOrgId}/dashboard`;

  const closeChatIfNeeded = (): void => {
    if (chatOpen) {
      handleViewChange("app");
    }
  };

  const goToWorkspaceTab = (tab: "products" | "projects"): void => {
    setMoreOpen(false);
    let href =
      tab === "products" ? defaultProductsHref : defaultProjectsHref;
    if (tab === "products") {
      const stored = readSessionPath(LAST_PRODUCTS_PATH_KEY);
      if (stored?.includes(`/${validOrgId}/products`)) {
        href = stored;
      }
    }
    closeChatIfNeeded();
    router.push(href);
  };

  const goToDashboardTab = (): void => {
    setMoreOpen(false);
    closeChatIfNeeded();
    router.push(dashboardHref);
  };

  const toggleAskKriya = (): void => {
    setMoreOpen(false);
    if (chatOpen) {
      handleViewChange("app");
      return;
    }
    handleViewChange("chat");
  };

  const sideTabs: ReadonlyArray<{
    id: MobileTabId;
    label: string;
    icon: typeof LayoutGrid;
    onSelect: () => void;
  }> = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      onSelect: goToDashboardTab,
    },
    {
      id: "products",
      label: "Products",
      icon: LayoutGrid,
      onSelect: () => {
        goToWorkspaceTab("products");
      },
    },
    {
      id: "projects",
      label: "Projects",
      icon: FolderKanban,
      onSelect: () => {
        goToWorkspaceTab("projects");
      },
    },
    {
      id: "more",
      label: "More",
      icon: MoreHorizontal,
      onSelect: () => {
        setMoreOpen(true);
      },
    },
  ];

  const leftTabs = sideTabs.slice(0, 2);
  const rightTabs = sideTabs.slice(2);

  const renderTab = (tab: (typeof sideTabs)[number]): JSX.Element => {
    const Icon = tab.icon;
    const isActive = activeTab === tab.id;
    return (
      <li key={tab.id} className="min-w-0">
        <button
          type="button"
          onClick={tab.onSelect}
          className={cn(
            "flex h-full w-full flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors",
            isActive
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
          aria-current={isActive ? "page" : undefined}
        >
          <Icon
            className={cn("size-5", isActive && "text-primary")}
            aria-hidden
          />
          <span className="truncate px-0.5">{tab.label}</span>
        </button>
      </li>
    );
  };

  return (
    <>
      <nav
        className="relative overflow-visible border-t border-border bg-background"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        aria-label="Mobile primary"
      >
        <ul className="grid h-14 grid-cols-5 items-end">
          {leftTabs.map(renderTab)}
          <li className="relative flex min-w-0 items-center justify-center">
            <GradientButton
              type="button"
              onClick={toggleAskKriya}
              disableAmination={chatOpen}
              variant={chatOpen ? "outline" : undefined}
              aria-label={chatOpen ? "Close Ask Kriya" : "Ask Kriya"}
              aria-pressed={chatOpen}
              className={cn(
                "absolute bottom-2 size-14 rounded-full p-0 shadow-lg",
                chatOpen
                  ? "border-2 border-primary bg-background text-primary"
                  : "text-white",
              )}
            >
              <Sparkles className="size-6" aria-hidden />
              <span className="sr-only">Ask Kriya</span>
            </GradientButton>
          </li>
          {rightTabs.map(renderTab)}
        </ul>
      </nav>
      {moreOpen ? (
        <MobileMoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
      ) : null}
    </>
  );
}
